import React, {useEffect, useState} from 'react';
import {format, fromUnixTime} from 'date-fns';

import {
	censorObscenities,
	getDebug,
	isMasterFirebaseUser,
} from '../library/GeneralUse';
import {
	hoursSinceLastPost,
	incrementPostCount,
	setLastPostDate,
} from '../library/FirestoreUser';
import {
	createNewsDocument,
	readNewsDocumentsLimit,
	refreshNewsDocuments,
} from '../library/NewsCollection';
import {
	depopulateDummyUsers,
	populateDummyUsers,
	readUsersDocumentsLimit,
	refreshUsersDocuments,
} from '../library/UsersCollection';
import {
	createPostDocument,
	readPostDocumentsLimit,
	refreshPostDocuments,
} from '../library/PostsCollection';

import {useFirebaseAuthUserStore} from '../stores/FirebaseAuthUserStore';
import {useFirestoreUserStore} from '../stores/FirestoreUserStore';
import {useNewsStore} from '../stores/NewsStore';
import {useUsersStore} from '../stores/UsersStore';
import {usePostsStore} from '../stores/PostsStore';

import {SubmitForm} from '../components/SubmitForm/SubmitForm';

import {Content} from '../components/PrimaryContent/Box/Content';
import {Header} from '../components/PrimaryContent/Box/Header';
import {PrimaryContentBox} from '../components/PrimaryContent/Box/PrimaryContentBox';
import {PrimaryContentRow} from '../components/PrimaryContent/PrimaryContentRow';

import {DiscussionRowContainer} from '../components/Community/Discussion/DiscussionRowContainer';
import {Post} from '../components/Community/Discussion/Posts/Post';
import {PostsContainer} from '../components/Community/Discussion/Posts/PostsContainer';
import {PostSubmitForm} from '../components/Community/Discussion/Posts/PostSubmitForm';
import {UserDisplay} from '../components/Community/Discussion/Users/UserDisplay';
import {UsersContainer} from '../components/Community/Discussion/Users/UsersContainer';

import {NewsContainer} from '../components/Community/News/NewsContainer';
import {NewsPost} from '../components/Community/News/NewsPost';

const style = {
	dummyButton: `flex justify-center items-center   p-2   text-whirlwindLightest hover:text-whirlwindLight bg-whirlwindPrimary hover:bg-whirlwindPrimaryDark leading-tight   rounded   opacity-90`,
	noFormMessage: `mt-4 p-4   text-center text-xl recommended:text-2xl text-whirlwindPrimaryLight`,
};

const DEBUG = getDebug('community');

export const Community = () => {
	// Zustand
	const firebaseAuthUser = useFirebaseAuthUserStore(
		(state) => state.firebaseAuthUser,
	);
	const firestoreUser = useFirestoreUserStore((state) => state.firestoreUser);
	const news = useNewsStore((state) => state.news);
	const users = useUsersStore((state) => state.users);
	const posts = usePostsStore((state) => state.posts);

	// Component variables
	// Consider using useRef for below so we are not re rendering on every keystroke?
	const [newsTitle, setNewsTitle] = useState('');
	const [newsPost, setNewsPost] = useState('');
	const [currentlyEditingNewsDocumentId, setCurrentlyEditingNewsDocumentId] =
		useState('');
	const [post, setPost] = useState('');

	const postFormFields = [
		{
			fieldVal: post,
			id: 'post',
			isTextArea: true,
			onlyForMaster: false,
			maxLength: 500,
			placeholder: 'Start typing post...',
			setFieldVal: setPost,
		},
	];

	const newsPostFormFields = [
		{
			fieldVal: newsTitle,
			id: 'newsTitle',
			isTextArea: false,
			onlyForMaster: true,
			maxLength: 50,
			placeholder: 'Start typing title...',
			setFieldVal: setNewsTitle,
		},
		{
			fieldVal: newsPost,
			id: 'newsPost',
			isTextArea: true,
			onlyForMaster: true,
			maxLength: 500,
			placeholder: 'Start typing news post...',
			setFieldVal: setNewsPost,
		},
	];

	// Component methods
	//
	// Render News SubmitForm only if we the master Firebase Auth user is signed in and their username is set
	const checkNewsSubmitForm = () => {
		DEBUG && console.trace(`checkNewsSubmitForm()`);

		try {
			// Zustand
			if (isMasterFirebaseUser(firebaseAuthUser?.uid)) {
				if (firestoreUser?.username) {
					return true;
				}
			}
		} catch (error) {
			DEBUG && console.error(error);
		}

		return false;
	};

	const handleNewsPostFormSubmit = async () => {
		DEBUG && console.clear();
		DEBUG && console.trace(`handleNewsPostFormSubmit()`);

		try {
			if (isMasterFirebaseUser(firebaseAuthUser?.uid)) {
				// Zustand
				const firebaseAuthUser =
					useFirebaseAuthUserStore.getState().firebaseAuthUser;

				const newsTitle = (
					document.getElementById('newsTitle') as HTMLInputElement
				).value;
				const newsPost = (
					document.getElementById('newsPost') as HTMLInputElement
				).value;

				if (newsPost && newsTitle && firebaseAuthUser) {
					const censoredNewsTitle = censorObscenities(newsTitle);
					const censoredNewsPost = censorObscenities(newsPost);

					await createNewsDocument(
						censoredNewsPost,
						censoredNewsTitle,
						firebaseAuthUser.uid,
						firestoreUser?.username,
					);

					setNewsTitle('');
					setNewsPost('');
					setCurrentlyEditingNewsDocumentId('');

					await refreshNewsDocuments(4);
				} else {
					DEBUG &&
						console.log(
							`newsTitle is empty string or newsPost is empty string or firebaseAuthUser is null`,
						);
				}
			}
		} catch (error) {
			DEBUG && console.error(error);
		}
	};

	const handlePostFormSubmit = async () => {
		DEBUG && console.clear();
		DEBUG && console.trace(`handlePostFormSubmit()`);

		try {
			// Zustand
			const firebaseAuthUser =
				useFirebaseAuthUserStore.getState().firebaseAuthUser;
			const firestoreUser = useFirestoreUserStore.getState().firestoreUser;

			const post = (document.getElementById('post') as HTMLInputElement)
				.value;

			if (post && firebaseAuthUser && firestoreUser) {
				const censoredPost = censorObscenities(post);
				await createPostDocument(
					censoredPost,
					firebaseAuthUser.uid,
					firestoreUser.username,
				);
				await incrementPostCount();
				await setLastPostDate();

				setPost('');

				await refreshPostDocuments(4);
			} else {
				DEBUG &&
					console.log(
						`post is empty string or firebaseAuthUser is null or firestoreUser is null`,
					);
			}
		} catch (error) {
			DEBUG && console.error(error);
		}
	};

	// Wrapper method to help us load data sequentially: news -> users -> posts
	const loadNews = async () => {
		try {
			await readNewsDocumentsLimit(4);
		} catch (error) {
			DEBUG && console.error(error);
			return false;
		}

		return true;
	};

	// Wrapper method to help us load data sequentially: news -> users -> posts
	const loadUsers = async (newsLoaded: boolean) => {
		if (newsLoaded) {
			try {
				await readUsersDocumentsLimit(16);
			} catch (error) {
				DEBUG && console.error(error);
				return false;
			}
		} else {
			return false;
		}

		return true;
	};

	// Wrapper method to help us load data sequentially: news -> users -> posts
	const loadPosts = async (postsLoaded: boolean) => {
		if (postsLoaded) {
			try {
				await readPostDocumentsLimit(4);
			} catch (error) {
				DEBUG && console.error(error);
				return false;
			}
		} else {
			return false;
		}

		return true;
	};

	// Synchronise app with Firestore
	useEffect(() => {
		const initialLoadData = async () => {
			try {
				// Enforce sequential data loading: news -> users -> posts
				const newsLoaded = await loadNews();
				const usersLoaded = await loadUsers(newsLoaded);
				await loadPosts(usersLoaded);
			} catch (error) {
				DEBUG && console.error(error);
			}
		};

		if (!news && !users && !posts) {
			initialLoadData();
		} else {
			DEBUG &&
				console.log(
					`News and Users and Posts are CURRENTLY LOADING or ALREADY LOADED!`,
				);
		}
	}, []);

	return (
		<PrimaryContentRow>
			<PrimaryContentBox tailwindcss={'basis-1/2 max-w-[50%]'}>
				<Header>Discussion</Header>
				<Content>
					<DiscussionRowContainer
						tailwindcss={`pb-4   min-h-48 recommended:min-h-96   max-h-72 recommended:max-h-[27rem]`}>
						<UsersContainer
							tailwindcss={'basis-1/4'}
							dataTestId={`usersContainer_TEST`}
							id={`usersContainer`}>
							{users?.map((user) =>
								user.data()?.username ? (
									<UserDisplay
										key={user.id}
										uid={user.id}
										username={user.data()?.username}
									/>
								) : (
									<></>
								),
							)}
						</UsersContainer>
						<PostsContainer
							tailwindcss={'basis-3/4'}
							dataTestId={`postsContainer_TEST`}
							id={`postsContainer`}>
							{posts?.map((post) => (
								// CAVEATS OF PASSING IN USERNAME INSTEAD OF UID?
								<Post
									content={post.data()?.content}
									created_at={format(
										fromUnixTime(post.data()?.created_at.seconds),
										'MMM d yyyy, hh:mm a',
									)}
									dataTestContent={post.data()?.content}
									documentId={post.id}
									key={post.id}
									uid={post.data()?.uid}
									username={post.data()?.username}
								/>
							))}
						</PostsContainer>
					</DiscussionRowContainer>
					<DiscussionRowContainer>
						<div className="flex basis-1/4 flex-col gap-y-1 pt-1 recommended:gap-y-4 recommended:pt-4">
							{isMasterFirebaseUser(firebaseAuthUser?.uid) ? (
								// Frankly, way overkill to make the below their own components.
								// Also, we want to update the list in real time to the user, upon populate/depopulate.
								<>
									<button
										className={style.dummyButton}
										data-test-id={`populateDummyUsersButton_TEST`}
										onClick={async () => {
											DEBUG && console.clear();
											await populateDummyUsers(41);
											setTimeout(() => {
												refreshUsersDocuments(16);
											}, 3000);
										}}>
										Populate dummy users
									</button>
									<button
										className={style.dummyButton}
										data-test-id={`depopulateDummyUsersButton_TEST`}
										onClick={async () => {
											DEBUG && console.clear();
											await depopulateDummyUsers(41);
											setTimeout(() => {
												refreshUsersDocuments(16);
											}, 3000);
										}}>
										Depopulate dummy users
									</button>
								</>
							) : (
								<></>
							)}
						</div>
						<div className="flex basis-3/4 justify-center">
							<PostSubmitForm
								fields={postFormFields}
								hoursSinceLastPostVal={hoursSinceLastPost()}
								onSubmit={handlePostFormSubmit}
							/>
						</div>
					</DiscussionRowContainer>
				</Content>
			</PrimaryContentBox>
			<PrimaryContentBox tailwindcss={'basis-1/2 max-w-[50%]'}>
				<Header>News</Header>
				<Content>
					<NewsContainer
						dataTestId={`newsContainer_TEST`}
						id={`newsContainer`}>
						{news?.map((newsDoc) => (
							<NewsPost
								content={newsDoc.data()?.content}
								created_at={format(
									fromUnixTime(newsDoc.data()?.created_at.seconds),
									'MMM d yyyy, hh:mm a',
								)}
								dataTestContent={newsDoc.data()?.content}
								dataTestTitle={newsDoc.data()?.title}
								documentId={newsDoc.id}
								key={newsDoc.id}
								last_edited_at={
									newsDoc.data()?.created_at.seconds !==
									newsDoc.data()?.last_edited_at.seconds
										? format(
												fromUnixTime(
													newsDoc.data()?.last_edited_at.seconds,
												),
												'MMM d yyyy, hh:mm a',
											)
										: null
								}
								title={newsDoc.data()?.title}
								uid={newsDoc.data()?.uid}
								username={newsDoc.data()?.username}
								currentlyEditingDocumentId={
									currentlyEditingNewsDocumentId
								}
								setCurrentlyEditingDocumentId={
									setCurrentlyEditingNewsDocumentId
								}
							/>
						))}
					</NewsContainer>
					{checkNewsSubmitForm() ? (
						<SubmitForm
							fields={newsPostFormFields}
							id={`newsPostForm`}
							onSubmit={handleNewsPostFormSubmit}
							tailwindcss={`pr-4 pt-4`}
						/>
					) : !isMasterFirebaseUser(firebaseAuthUser?.uid) ? (
						<></>
					) : (
						<span className={style.noFormMessage}>Set username</span>
					)}
				</Content>
			</PrimaryContentBox>
		</PrimaryContentRow>
	);
};
